/**
 * @module browser-storage-adapter
 * @description Implements the local-first payments adapter used for demos,
 * prototyping, and Storybook scenarios. The adapter stores plans and
 * subscriptions in guarded browser storage while mirroring the payments contract.
 */

import {
  type BrowserStorage,
  getBrowserLocalStorage,
  isBrowserStorageQuotaError,
} from "../browser-storage";
import { PaymentsError } from "../errors";
import { warnRuntimeAdapterMock } from "../runtime-warning";
import type {
  CheckoutParams,
  CheckoutResult,
  Plan,
  Subscription,
} from "../types";
import type { PaymentsAdapter } from "./interface";

const SUBS_KEY = "geenius_subscriptions";
const PLANS_KEY = "geenius_plans";

const DEFAULT_PLANS: Plan[] = [
  {
    id: "free",
    name: "Free",
    price: 0,
    currency: "USD",
    interval: "month",
    features: ["basic-features", "single-project", "community-support"],
  },
  {
    id: "pro",
    name: "Pro",
    price: 29,
    currency: "USD",
    interval: "month",
    features: [
      "basic-features",
      "unlimited-projects",
      "priority-support",
      "ai-features",
      "pdf-export",
      "api-access",
    ],
  },
  {
    id: "team",
    name: "Team",
    price: 79,
    currency: "USD",
    interval: "month",
    features: [
      "basic-features",
      "unlimited-projects",
      "priority-support",
      "ai-features",
      "pdf-export",
      "api-access",
      "team-sharing",
      "admin-dashboard",
      "custom-integrations",
      "custom-domain",
      "sso",
    ],
  },
];

const LEGACY_FEATURE_ALIASES: Record<string, readonly string[]> = {
  "1 project": ["single-project"],
  "admin dashboard": ["admin-dashboard"],
  "ai features": ["ai-features"],
  "all free features": [
    "basic-features",
    "single-project",
    "community-support",
  ],
  "all pro features": [
    "basic-features",
    "unlimited-projects",
    "priority-support",
    "ai-features",
    "pdf-export",
    "api-access",
  ],
  "basic features": ["basic-features"],
  "community support": ["community-support"],
  "custom integrations": ["custom-integrations"],
  "priority support": ["priority-support"],
  "team management": ["team-sharing"],
  "unlimited projects": ["unlimited-projects"],
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPlan(value: unknown): value is Plan {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    typeof value.price === "number" &&
    typeof value.currency === "string" &&
    (value.interval === "month" ||
      value.interval === "year" ||
      value.interval === "one-time") &&
    Array.isArray(value.features) &&
    value.features.every((feature: unknown) => typeof feature === "string")
  );
}

function isSubscription(value: unknown): value is Subscription {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    typeof value.userId === "string" &&
    typeof value.planId === "string" &&
    ["active", "cancelled", "past_due", "trialing"].includes(
      String(value.status),
    ) &&
    typeof value.currentPeriodEnd === "string" &&
    typeof value.cancelAtPeriodEnd === "boolean"
  );
}

function parseSubscriptions(raw: string | null): Subscription[] {
  const parsed: unknown = JSON.parse(raw || "[]");
  if (!Array.isArray(parsed) || !parsed.every(isSubscription)) {
    throw new PaymentsError(
      "Invalid subscriptions payload in browser storage",
      "SERVICE_ERROR",
    );
  }
  return parsed;
}

function parsePlans(raw: string | null): Plan[] {
  const parsed: unknown = JSON.parse(raw || "[]");
  if (!Array.isArray(parsed) || !parsed.every(isPlan)) {
    throw new PaymentsError(
      "Invalid plans payload in browser storage",
      "SERVICE_ERROR",
    );
  }
  return parsed;
}

function normalizeFeatureId(feature: string): string {
  return feature.trim().toLowerCase();
}

function getFeatureLookupIds(feature: string): ReadonlySet<string> {
  const normalized = normalizeFeatureId(feature);
  return new Set([normalized, ...(LEGACY_FEATURE_ALIASES[normalized] ?? [])]);
}

function getPlanFeatureIds(plan: Plan): ReadonlySet<string> {
  const features = new Set<string>();
  for (const feature of plan.features) {
    const normalized = normalizeFeatureId(feature);
    features.add(normalized);
    for (const alias of LEGACY_FEATURE_ALIASES[normalized] ?? []) {
      features.add(alias);
    }
  }
  return features;
}

function getSubs(): Subscription[] {
  try {
    const storage = getBrowserLocalStorage();
    return parseSubscriptions(storage.getItem(SUBS_KEY));
  } catch (error) {
    if (error instanceof PaymentsError) {
      throw error;
    }
    if (error instanceof SyntaxError) {
      throw new PaymentsError(
        "Failed to parse subscriptions from browser storage",
        "SERVICE_ERROR",
        error,
      );
    }
    throw new PaymentsError(
      "Failed to read from browser storage",
      "SERVICE_ERROR",
      error,
    );
  }
}

function saveSubs(subs: Subscription[]): void {
  try {
    const storage = getBrowserLocalStorage();
    storage.setItem(SUBS_KEY, JSON.stringify(subs));
  } catch (error) {
    if (isBrowserStorageQuotaError(error)) {
      throw new PaymentsError(
        "browser storage quota exceeded",
        "SERVICE_ERROR",
        error,
      );
    }
    throw new PaymentsError(
      "Failed to write to browser storage",
      "SERVICE_ERROR",
      error,
    );
  }
}

function getPlansFromStorage(): Plan[] {
  try {
    const storage = getBrowserLocalStorage();
    seedDefaultPlans(storage);
    const rawPlans = storage.getItem(PLANS_KEY);
    return rawPlans ? parsePlans(rawPlans) : DEFAULT_PLANS;
  } catch (error) {
    if (error instanceof PaymentsError) {
      throw error;
    }
    throw new PaymentsError(
      "Failed to read plans from browser storage",
      "SERVICE_ERROR",
      error,
    );
  }
}

function seedDefaultPlans(storage: BrowserStorage): void {
  if (storage.getItem(PLANS_KEY)) {
    return;
  }

  try {
    storage.setItem(PLANS_KEY, JSON.stringify(DEFAULT_PLANS));
  } catch (error) {
    if (isBrowserStorageQuotaError(error)) {
      throw new PaymentsError(
        "browser storage quota exceeded",
        "SERVICE_ERROR",
        error,
      );
    }
    throw new PaymentsError(
      "Failed to initialize plans",
      "SERVICE_ERROR",
      error,
    );
  }
}

/**
 * Creates the mock browser-storage-backed payments adapter used in demos and Storybook.
 *
 * @returns A payments adapter implementation that stores plans and subscriptions in browser storage.
 * @throws {PaymentsError} When plans or subscriptions cannot be persisted during method calls.
 */
export function createLocalStoragePaymentsAdapter(): PaymentsAdapter {
  if (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV === "production"
  ) {
    warnRuntimeAdapterMock(
      "[@geenius/adapters] browser storage payments adapter is a MOCK for prototyping only. " +
        "Do NOT use in production. Use a real payments provider from @geenius/payments instead.",
    );
  }

  return {
    async createCheckout(params: CheckoutParams) {
      const plan = getPlansFromStorage().find(
        (candidate) => candidate.id === params.planId,
      );
      if (!plan) {
        throw new PaymentsError(
          `Unknown plan: ${params.planId}`,
          "INVALID_PLAN",
        );
      }

      // Mock: instantly create a subscription
      const subs = getSubs();
      const sub: Subscription = {
        id: crypto.randomUUID(),
        userId: params.userId,
        planId: plan.id,
        status: "active",
        currentPeriodEnd: new Date(
          Date.now() + 30 * 24 * 60 * 60 * 1000,
        ).toISOString(),
        cancelAtPeriodEnd: false,
      };
      subs.push(sub);
      saveSubs(subs);
      const result: CheckoutResult = {
        url: params.successUrl || "/",
        sessionId: sub.id,
      };
      return result;
    },

    async getSubscription(userId) {
      const sub = getSubs().find(
        (s) => s.userId === userId && s.status === "active",
      );
      return sub ?? null;
    },

    async cancelSubscription(subscriptionId) {
      const subs = getSubs();
      const idx = subs.findIndex((s) => s.id === subscriptionId);
      if (idx === -1) {
        throw new PaymentsError(
          `Subscription not found: ${subscriptionId}`,
          "SUBSCRIPTION_NOT_FOUND",
        );
      }

      const subscription = subs[idx];
      if (!subscription) {
        throw new PaymentsError(
          `Subscription not found: ${subscriptionId}`,
          "SUBSCRIPTION_NOT_FOUND",
        );
      }
      subscription.cancelAtPeriodEnd = true;
      subscription.status = "cancelled";
      saveSubs(subs);
    },

    async getPlans() {
      return getPlansFromStorage();
    },

    async isFeatureEnabled(userId, feature) {
      const sub = await this.getSubscription(userId);
      if (!sub) return false;
      const plans: Plan[] = await this.getPlans();
      const plan = plans.find((p) => p.id === sub.planId);
      if (!plan) return false;

      const enabledFeatures = getPlanFeatureIds(plan);
      return [...getFeatureLookupIds(feature)].some((featureId) =>
        enabledFeatures.has(featureId),
      );
    },
  };
}

/**
 * Creates a payments adapter that accepts billing operations without contacting
 * a provider.
 *
 * @returns A no-op payments adapter used by CI, tests, and tiers that intentionally disable billing flows.
 */
export function createNoopPaymentsAdapter(): PaymentsAdapter {
  return {
    async createCheckout(params) {
      return {
        url: params.successUrl ?? "/",
        sessionId: `noop_${params.userId}_${params.planId}`,
      };
    },
    async getSubscription() {
      return null;
    },
    async cancelSubscription() {},
    async getPlans() {
      return [];
    },
    async isFeatureEnabled() {
      return false;
    },
  };
}
